import React, { useEffect, useState } from 'react'
import QRCodeGenerator from 'qrcode'
import toast from 'react-hot-toast'
import { OpenInNew } from '@mui/icons-material'
import { Client, Enrollment, EnrollmentPaymentStatus } from '../../../../pure-js/types/GrooverTypes'
import { formatGrooverText } from '../../libs/GrooverTextRepository'
import {
  canShowPortalEnrollmentPayNow,
  getEnrollmentLocationName,
  getEnrollmentQrValue,
  getEnrollmentStatusLabel,
  getEnrollmentTitle,
  getPaymentStatusTextKey,
  getPortalEnrollmentPaymentPath,
  getPortalEnrollmentChildNames,
  getPortalHardCouplePartnerName,
  handlePortalPayNowClick
} from './portalEnrollmentHelpers'
import { QrExpandedDialog } from '../../components/qr/QrExpandedDialog'
import {
  CardHeroRow,
  CardMeta,
  CardStatusPills,
  CardTitle,
  CardTopRow,
  EnrollmentCard,
  PayEnrollmentLinkCompact,
  QrThumbButton,
  QrThumbColumn,
  QrThumbInner,
  QrThumbPlaceholder,
  SchoolSubtitle,
  StatusPill,
  TapForQrButton
} from './PortalEnrollmentsPage.styles'

export type PortalEnrollmentGetText = (key: string) => string

interface PortalEnrollmentCardProps {
  enrollment: Enrollment
  client: Client | null
  getText: PortalEnrollmentGetText
  onClick: () => void
  showSchool?: boolean
  enrollmentUserId?: string
  lang?: string
  isViewAs?: boolean
}

export const PortalEnrollmentCard: React.FC<PortalEnrollmentCardProps> = ({
  enrollment,
  client,
  getText,
  onClick,
  showSchool = true,
  enrollmentUserId,
  lang,
  isViewAs = false
}) => {
  const qrCodeValue = getEnrollmentQrValue(enrollment, client)
  const [pngDataUrl, setPngDataUrl] = useState<string | null>(null)
  const [showQrExpanded, setShowQrExpanded] = useState(false)
  const activity = enrollment.activities?.[0]
  const locationName = getEnrollmentLocationName(activity, client)
  const title = getEnrollmentTitle(enrollment, getText('portalEnrollments'))
  const partnerName = getPortalHardCouplePartnerName(enrollment, enrollmentUserId)
  const childNames = getPortalEnrollmentChildNames(enrollment)
  const activityCount = enrollment.activities?.length ?? 0
  const paymentPath = canShowPortalEnrollmentPayNow(enrollment)
    ? getPortalEnrollmentPaymentPath(enrollment, { enrollmentUserId, lang })
    : null

  useEffect(() => {
    if (!qrCodeValue) {
      setPngDataUrl(null)
      return
    }
    let cancelled = false
    QRCodeGenerator.toDataURL(qrCodeValue, {
      errorCorrectionLevel: 'H',
      margin: 0,
      width: 84,
      color: { dark: '#000000', light: '#FFFFFF' }
    })
      .then((url) => {
        if (!cancelled) setPngDataUrl(url)
      })
      .catch(() => {
        if (!cancelled) setPngDataUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [qrCodeValue])

  return (
    <EnrollmentCard
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <CardTopRow>
        {activityCount > 1 ? (
          <CardTitle>
            {formatGrooverText(getText('portalEnrollmentCardActivityCount'), [String(activityCount)])}
          </CardTitle>
        ) : (
          <CardTitle>{title}</CardTitle>
        )}
        <CardStatusPills>
          <StatusPill $status={enrollment.status}>{getEnrollmentStatusLabel(enrollment.status, getText)}</StatusPill>
          <StatusPill $status={enrollment.paymentStatus || EnrollmentPaymentStatus.Unpaid}>
            {getText(getPaymentStatusTextKey(enrollment.paymentStatus))}
          </StatusPill>
        </CardStatusPills>
      </CardTopRow>
      {activityCount > 1 && (
        <SchoolSubtitle>
          {(enrollment.activities ?? []).map((listedActivity) => (
            <div key={listedActivity.id}>{listedActivity.name || getText('portalEnrollments')}</div>
          ))}
        </SchoolSubtitle>
      )}
      {showSchool && client?.name && <SchoolSubtitle>{client.name}</SchoolSubtitle>}
      {childNames && (
        <SchoolSubtitle>
          {formatGrooverText(getText('portalEnrollmentRegisteredForChild'), [childNames])}
        </SchoolSubtitle>
      )}
      {partnerName && (
        <SchoolSubtitle>
          {formatGrooverText(getText('portalEnrollmentRegisteredTogether'), [partnerName])}
        </SchoolSubtitle>
      )}
      <CardHeroRow>
        <CardMeta>
          {locationName && <span>{locationName}</span>}
          {activity?.noOfOccasions ? (
            <span>
              {activity.noOfOccasions} {getText('portalEnrollmentOccasions').toLowerCase()}
            </span>
          ) : null}
          {paymentPath && (
            <PayEnrollmentLinkCompact
              href={isViewAs ? '#' : paymentPath}
              target={isViewAs ? undefined : '_blank'}
              rel={isViewAs ? undefined : 'noopener noreferrer'}
              onClick={(e) => {
                if (!handlePortalPayNowClick(e, isViewAs)) {
                  toast.error(getText('portalEnrollmentPayNowReadOnly'), { duration: 4000 })
                }
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {getText('portalEnrollmentPayNow')}
            </PayEnrollmentLinkCompact>
          )}
        </CardMeta>
        {qrCodeValue && (
          <QrThumbColumn>
            <QrThumbButton
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setShowQrExpanded(true)
              }}
              aria-label={getText('portalEnrollmentTapToEnlarge')}
            >
              <QrThumbInner>
                {pngDataUrl ? (
                  <img src={pngDataUrl} alt="" width={84} height={84} />
                ) : (
                  <QrThumbPlaceholder aria-hidden />
                )}
              </QrThumbInner>
            </QrThumbButton>
            <TapForQrButton
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
            >
              <OpenInNew style={{ fontSize: 14 }} aria-hidden />
              {getText('portalEnrollmentTapForQr')}
            </TapForQrButton>
          </QrThumbColumn>
        )}
      </CardHeroRow>
      <QrExpandedDialog
        open={showQrExpanded}
        value={qrCodeValue ?? ''}
        onClose={() => setShowQrExpanded(false)}
        closeLabel={getText('portalClose')}
        instruction={getText('portalEnrollmentShowQrAtStudio')}
        labelledBy={`portal-enrollment-qr-${enrollment.id}`}
      />
    </EnrollmentCard>
  )
}
